import React, { useMemo, useState } from 'react';

/**
 * BlueprintFlowChart — Visual workflow map using HTML/CSS positioned nodes.
 * No SVG scaling issues. Nodes are always full-size and readable.
 */

const NODE_W = 220;
const NODE_H = 56;
const GAP_Y = 48;

const TYPE_STYLES = {
  action:   { bg: 'bg-emerald-950 border-emerald-500/40', text: 'text-emerald-400', badge: 'bg-emerald-500/20 text-emerald-400', label: 'ACTION' },
  decision: { bg: 'bg-amber-950 border-amber-500/40', text: 'text-amber-400', badge: 'bg-amber-500/20 text-amber-400', label: 'DECISION' },
  wait:     { bg: 'bg-indigo-950 border-indigo-500/40', text: 'text-indigo-400', badge: 'bg-indigo-500/20 text-indigo-400', label: 'WAIT' },
  trigger:  { bg: 'bg-emerald-950/80 border-emerald-400/50', text: 'text-emerald-300', badge: 'bg-emerald-500/20 text-emerald-400', label: 'TRIGGER' },
  output:   { bg: 'bg-cyan-950 border-cyan-500/40', text: 'text-cyan-400', badge: 'bg-cyan-500/20 text-cyan-400', label: 'OUTPUT' },
};

function buildLayout(triggers, states, outputSchema) {
  const rows = [];
  
  // Row 0: Triggers
  if (triggers?.length > 0) {
    rows.push({ type: 'triggers', items: triggers.map((t, i) => ({ id: `trigger-${i}`, label: t.label || t.type, type: 'trigger' })) });
  }

  // BFS to determine depths
  const stateMap = {};
  (states || []).forEach(s => { stateMap[s.id] = s; });
  const referenced = new Set();
  (states || []).forEach(s => (s.next || []).forEach(n => referenced.add(n)));
  const roots = (states || []).filter(s => !referenced.has(s.id));
  if (roots.length === 0 && states?.length > 0) roots.push(states[0]);

  const depths = {};
  const visited = new Set();
  const queue = [...roots.map(r => r.id)];
  roots.forEach(r => { depths[r.id] = 0; visited.add(r.id); });

  while (queue.length > 0) {
    const cur = queue.shift();
    const state = stateMap[cur];
    if (!state) continue;
    (state.next || []).forEach(nid => {
      if (!visited.has(nid) && nid !== 'end') {
        visited.add(nid);
        depths[nid] = (depths[cur] || 0) + 1;
        queue.push(nid);
      }
    });
  }
  (states || []).forEach(s => { if (!(s.id in depths)) depths[s.id] = Object.keys(depths).length; });

  const byDepth = {};
  Object.entries(depths).forEach(([id, d]) => { if (!byDepth[d]) byDepth[d] = []; byDepth[d].push(id); });

  Object.keys(byDepth).sort((a, b) => a - b).forEach(depth => {
    const ids = byDepth[depth];
    rows.push({
      type: 'states',
      items: ids.map(id => {
        const s = stateMap[id];
        return s ? { id: s.id, label: s.name || s.id, desc: s.description?.slice(0, 45), type: s.type || 'action', next: s.next || [] } : null;
      }).filter(Boolean),
    });
  });

  // Output row
  if (outputSchema && Object.keys(outputSchema).length > 0) {
    rows.push({
      type: 'output',
      items: [{ id: 'output', label: 'Output', desc: Object.keys(outputSchema).slice(0, 3).join(', '), type: 'output' }],
    });
  }

  return rows;
}

export const BlueprintFlowChart = ({ triggers, states, outputSchema, onNodeClick }) => {
  const [hoveredNode, setHoveredNode] = useState(null);
  const rows = useMemo(() => buildLayout(triggers, states, outputSchema), [triggers, states, outputSchema]);

  if (!states || states.length === 0) {
    return (
      <div className="bg-[#0a0a0a] border border-white/5 p-8 text-center text-sm text-muted-foreground">
        No states to visualize. Generate a blueprint first.
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0a] border border-white/5" data-testid="flow-chart">
      <div className="p-3 border-b border-white/5 flex items-center justify-between">
        <h3 className="text-xs text-muted-foreground uppercase tracking-wider">Workflow Map</h3>
        <div className="flex gap-3">
          {['action', 'decision', 'wait', 'trigger'].map(key => (
            <div key={key} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-sm ${TYPE_STYLES[key].badge}`} />
              <span className="text-[10px] text-muted-foreground uppercase">{TYPE_STYLES[key].label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="p-6 overflow-x-auto" style={{ background: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)', backgroundSize: '24px 24px' }}>
        <div className="space-y-0">
          {rows.map((row, rowIdx) => (
            <div key={rowIdx}>
              {/* Connector lines from previous row */}
              {rowIdx > 0 && (
                <div className="flex justify-center py-2">
                  <div className="w-px h-6 bg-white/10" />
                </div>
              )}
              {/* Node row */}
              <div className="flex justify-center gap-4 flex-wrap">
                {row.items.map(node => {
                  const style = TYPE_STYLES[node.type] || TYPE_STYLES.action;
                  const isHovered = hoveredNode === node.id;
                  return (
                    <div
                      key={node.id}
                      onMouseEnter={() => setHoveredNode(node.id)}
                      onMouseLeave={() => setHoveredNode(null)}
                      onClick={() => onNodeClick?.(node)}
                      className={`border p-3 transition-all cursor-pointer ${style.bg} ${isHovered ? 'border-white/40 scale-105' : ''}`}
                      style={{ width: NODE_W, minHeight: NODE_H }}
                      data-testid={`node-${node.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-white leading-tight">{node.label}</p>
                        <span className={`text-[8px] font-mono px-1 py-0.5 flex-shrink-0 ${style.badge}`}>{style.label}</span>
                      </div>
                      {node.desc && <p className="text-[10px] text-white/40 mt-1 leading-tight">{node.desc}</p>}
                      {node.next?.length > 0 && (
                        <p className="text-[9px] text-white/20 mt-1.5 font-mono">
                          {node.next.map(n => n === 'end' ? 'END' : n).join(' | ')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Branching indicators */}
              {row.items.length > 1 && rowIdx > 0 && (
                <div className="flex justify-center mt-1">
                  <span className="text-[9px] text-white/15 font-mono">PARALLEL</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlueprintFlowChart;
